import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash/fp';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import {
  BuilderName,
  BuilderPreviewMap,
  BuilderPreviewSteps,
  InquiryTemplate,
  InquiryTemplateBuilder,
  InquiryTemplateFilterOption,
  InquiryTemplateStatus,
} from 'dashboard/models/inquiry-template';
import { PartialInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { TemplateFilterOption, TemplateListPartial } from 'dashboard/models/template';
import { PaginationParams } from 'lib/interfaces/api';

export interface InquiryTemplatesFilterParams {
  omni?: string;
  status?: InquiryTemplateStatus[];
}

export const initialInquiryTemplatesFilterState: InquiryTemplatesFilterParams = {
  status: [InquiryTemplateStatus.Active],
  omni: '',
};

export interface InquiryTemplatesState extends TableState<InquiryTemplatesFilterParams> {
  inquiryTemplates: InquiryTemplate[];
  inquiryTemplateFilters: InquiryTemplateFilterOption[];
  inquiryTemplateBuilders: InquiryTemplateBuilder[];
  legacyTemplates: TemplateListPartial[];
  legacyTemplateFilters: TemplateFilterOption[];
  templateBuilderPreviews: BuilderPreviewMap;
  partialInquiryTemplateVersions: PartialInquiryTemplateVersion[];
}

export const initialState: InquiryTemplatesState = {
  inquiryTemplates: [],
  inquiryTemplateFilters: [],
  inquiryTemplateBuilders: [],
  legacyTemplates: [],
  legacyTemplateFilters: [],
  partialInquiryTemplateVersions: [],
  templateBuilderPreviews: {},
  filters: initialInquiryTemplatesFilterState,
  sortParams: {
    sort: 'updated_at',
    shouldSortDescending: true,
  },
  paginationParams: undefined,
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
};

interface SetFiltersPayload {
  filters: InquiryTemplatesFilterParams;
}

interface SetSortParamsPayload {
  sortParams: SortParams;
}

interface SetCurrentPagePayload {
  currentPage: number;
}

interface SetPageSizePayload {
  pageSize: number;
}

interface SetInquiryTemplatesPayload {
  inquiryTemplates: InquiryTemplate[];
  paginationParams: PaginationParams;
}

interface SetInquiryTemplateFiltersPayload {
  inquiryTemplateFilters: InquiryTemplateFilterOption[];
}

interface SetLegacyTemplatesPayload {
  templates: TemplateListPartial[];
}

interface SetLegacyTemplateFiltersPayload {
  templateFilters: TemplateFilterOption[];
}

interface SetPartialInquiryTemplateVersionsPayload {
  partialInquiryTemplateVersions: PartialInquiryTemplateVersion[];
}

interface SetInquiryTemplateBuildersPayload {
  inquiryTemplateBuilders: InquiryTemplateBuilder[];
}

interface SetTemplateBuilderPreviewPayload {
  builderName: BuilderName;
  steps: BuilderPreviewSteps;
}

const { reducer, actions } = createSlice({
  name: 'inquiry-templates',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };
      return isEqual(newFilters, state.filters)
        ? state
        : {
            ...state,
            filters: newFilters,
          };
    },
    setSortParams: (state, action: PayloadAction<SetSortParamsPayload>) => {
      return {
        ...state,
        sortParams: {
          ...action.payload.sortParams,
        },
      };
    },
    setCurrentPage: (state, action: PayloadAction<SetCurrentPagePayload>) => {
      state.currentPage = action.payload.currentPage;
    },
    setPageSize: (state, action: PayloadAction<SetPageSizePayload>) => {
      state.pageSize = action.payload.pageSize;
    },
    setInquiryTemplates: (state, action: PayloadAction<SetInquiryTemplatesPayload>) => {
      state.inquiryTemplates = action.payload.inquiryTemplates;
      state.paginationParams = action.payload.paginationParams;
    },
    setInquiryTemplateFilters: (state, action: PayloadAction<SetInquiryTemplateFiltersPayload>) => {
      state.inquiryTemplateFilters = action.payload.inquiryTemplateFilters;
    },
    setLegacyTemplates: (state, action: PayloadAction<SetLegacyTemplatesPayload>) => {
      state.legacyTemplates = action.payload.templates;
    },
    setLegacyTemplateFilters: (state, action: PayloadAction<SetLegacyTemplateFiltersPayload>) => {
      state.legacyTemplateFilters = action.payload.templateFilters;
    },
    setPartialInquiryTemplateVersions: (
      state,
      action: PayloadAction<SetPartialInquiryTemplateVersionsPayload>,
    ) => {
      state.partialInquiryTemplateVersions = action.payload.partialInquiryTemplateVersions;
    },
    setInquiryTemplateBuilders: (
      state,
      action: PayloadAction<SetInquiryTemplateBuildersPayload>,
    ) => {
      state.inquiryTemplateBuilders = action.payload.inquiryTemplateBuilders;
    },
    setTemplateBuilderPreview: (state, action: PayloadAction<SetTemplateBuilderPreviewPayload>) => {
      state.templateBuilderPreviews[action.payload.builderName] = action.payload.steps;
    },
  },
});

export const {
  setFilters,
  setSortParams,
  setCurrentPage,
  setPageSize,
  setInquiryTemplates,
  setInquiryTemplateFilters,
  setLegacyTemplates,
  setLegacyTemplateFilters,
  setPartialInquiryTemplateVersions,
  setInquiryTemplateBuilders,
  setTemplateBuilderPreview,
} = actions;

export const inquiryTemplatesReducer = reducer;
